const BASE = '/api/v1'

export const AUTH_TOKEN_KEY = 'novus_token'
export const ADMIN_AUTH_TOKEN_KEY = 'novus_admin_token'

class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown) {
    super(`HTTP ${status}`)
    this.status = status
    this.body = body
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

function isAdminPath(path: string): boolean {
  return path.startsWith('/admin/')
}

function buildHeaders(path: string): HeadersInit {
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }

  const token = localStorage.getItem(isAdminPath(path) ? ADMIN_AUTH_TOKEN_KEY : AUTH_TOKEN_KEY)
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function handleUnauthorized(path: string) {
  if (isAdminPath(path)) {
    if (localStorage.getItem(ADMIN_AUTH_TOKEN_KEY)) {
      localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY)
    }
    const currentPath = window.location.pathname + window.location.search
    if (!currentPath.startsWith('/admin/login')) {
      window.location.href = `/admin/login?from=${encodeURIComponent(currentPath)}`
    }
    return
  }

  // 401: 清 token + 跳登录。带上 from 让登录后能回到原页面。
  if (localStorage.getItem(AUTH_TOKEN_KEY)) {
    localStorage.removeItem(AUTH_TOKEN_KEY)
  }
  const currentPath = window.location.pathname + window.location.search
  if (!currentPath.startsWith('/login') && !currentPath.startsWith('/register')) {
    window.location.href = `/login?from=${encodeURIComponent(currentPath)}`
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: { params?: Record<string, string | number | undefined>; body?: unknown; retries?: number; timeoutMs?: number; headers?: Record<string, string> } = {},
): Promise<T> {
  const { params, body, retries = method === 'GET' ? 2 : 0, timeoutMs = 30000, headers: extraHeaders } = options
  const url = new URL(BASE + path, window.location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
  }

  let lastError: unknown

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

      const res = await fetch(url.toString(), {
        method,
        headers: { ...buildHeaders(path), ...extraHeaders },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!res.ok) {
        const text = await res.text()
        let parsed: unknown
        try { parsed = JSON.parse(text) } catch { parsed = text }
        if (res.status === 401) handleUnauthorized(path)
        throw new ApiError(res.status, parsed)
      }

      // 204 No Content (e.g. DELETE) → return empty object
      if (res.status === 204) return {} as T
      return await res.json()
    } catch (error) {
      lastError = error

      // 4xx 不重试
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        throw error
      }

      if (attempt === retries) {
        throw error
      }

      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
    }
  }

  throw lastError!
}

async function get<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
  retries = 2,
  timeoutMs = 30000,
): Promise<T> {
  return request<T>('GET', path, { params, retries, timeoutMs })
}

async function post<T>(path: string, body?: unknown, timeoutMs?: number): Promise<T> {
  return request<T>('POST', path, { body, timeoutMs })
}

async function postWithHeaders<T>(
  path: string,
  body: unknown,
  headers: Record<string, string>,
  timeoutMs?: number,
): Promise<T> {
  return request<T>('POST', path, { body, headers, timeoutMs })
}

async function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, { body })
}

async function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, { body })
}

async function del<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  return request<T>('DELETE', path, { params })
}

export { ApiError, post, postWithHeaders, put, patch, del }
export default get
